import React, { useCallback, useMemo, useRef } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { InboxItem, markAllInboxRead } from '../../services/api/inboxApi';
import { markActionRead, markAllActionsRead } from '../../services/api/actionsApi';
import type { ActionItem } from '../../services/api/actionsApi';
import { createSession } from '../../services/chatApi';
import type { ChatSession } from '../../services/chatApi';
import { showError } from '../../utils/alert';
import { useInbox, INBOX_KEY } from '../../services/queries/useInbox';
import { useActions, ACTIONS_KEY } from '../../services/queries/useActions';
import { invalidateNotificationCounts } from '../../services/queries/useNotificationCounts';
import {
  toFeedEntries,
  FeedEntry,
  discussSeedOf,
  discussSessionTitle,
} from '../../components/feed/feedItems';
import { FeedList } from '../../components/feed/FeedList';

/**
 * Feed tab (chat-centric revamp §5): one unified, read-mostly stream over
 * inbox_items + open action_items with Today/Earlier grouping, per-item
 * unread dots (read_at) and a header "mark all read" across both stores.
 */
export function FeedScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const navigation = useNavigation<any>();

  const {
    data: inboxData,
    isLoading: inboxLoading,
    isError: inboxError,
    isRefetching: inboxRefetching,
    refetch: refetchInbox,
  } = useInbox(50, 0);
  const {
    data: actionsData,
    isLoading: actionsLoading,
    isError: actionsError,
    isRefetching: actionsRefetching,
    refetch: refetchActions,
  } = useActions('open');
  const queryClient = useQueryClient();

  // Same invalidation set the per-store hooks use — counts stay in sync with
  // the tab badge (read-aware per Task 3).
  const markAllRead = useMutation({
    mutationFn: () => Promise.all([markAllInboxRead(), markAllActionsRead()]),
    onSuccess: () => {
      invalidateNotificationCounts(queryClient);
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: ACTIONS_KEY });
    },
  });

  const markActionReadMutation = useMutation({
    mutationFn: (id: number) => markActionRead(id),
    onSuccess: () => {
      invalidateNotificationCounts(queryClient);
      queryClient.invalidateQueries({ queryKey: ACTIONS_KEY });
    },
  });

  const refetchAll = useCallback(() => {
    refetchInbox();
    refetchActions();
  }, [refetchInbox, refetchActions]);

  useFocusEffect(refetchAll);

  const entries = useMemo(
    () => toFeedEntries(inboxData?.items ?? [], actionsData ?? [], t),
    [inboxData, actionsData, t]
  );

  const unreadCount = entries.filter((e) => e.unread).length;

  // Double-tap gate shared by both session-creating shortcuts (复盘 + 💬 讨论):
  // set synchronously at handler entry, cleared in finally. Without it a rapid
  // double-tap fires two createSession calls before the first await resolves —
  // duplicate sessions, one orphaned empty session, racing navigations.
  const creatingSessionRef = useRef(false);

  const openItem = useCallback(
    (entry: FeedEntry) => {
      if (entry.kind === 'inbox') {
        // InboxReading marks the item read on load (existing flow).
        navigation.navigate('Notifications', {
          screen: 'InboxReading',
          params: { itemId: (entry.item as InboxItem).id },
        });
      } else {
        markActionReadMutation.mutate((entry.item as { id: number }).id);
      }
    },
    [navigation, markActionReadMutation]
  );

  // ── Alert 复盘 shortcut (chat-centric revamp T7) ──
  // Same new-session shape as the thesis forms' "Build with TopNote": create a
  // titled session, seed the sessions cache BEFORE navigating (bug A, 2026-10-04),
  // then hand the chat shell a structured build_thesis command. No target rides
  // along — the position resolves at save time per the thesis spec. Symbol-less
  // alerts never get here: their shortcut renders disabled in FeedItem.
  const handleReviewThesis = useCallback(
    async (entry: FeedEntry) => {
      const symbol = (entry.item as ActionItem).symbol;
      if (!symbol) return;
      if (creatingSessionRef.current) return;
      creatingSessionRef.current = true;
      try {
        const session = await createSession(`📐 ${symbol} thesis`);
        queryClient.setQueryData<ChatSession[]>(['chat-sessions', 'active'], (old) =>
          old ? [session, ...old] : [session]
        );
        void queryClient.invalidateQueries({ queryKey: ['chat-sessions', 'active'] });
        navigation.navigate('Chat', {
          screen: 'ChatScreen',
          params: {
            sessionId: session.id,
            initialCommand: { command: 'build_thesis' as const, symbol },
          },
        });
      } catch (e) {
        showError((e as Error).message);
      } finally {
        creatingSessionRef.current = false;
      }
    },
    [navigation, queryClient]
  );

  // ── 💬 讨论 shortcut (chat-centric revamp T20, §6.3; revised 2026-10-09) ──
  // Every row converts into a discussion: same new-session shape as 复盘 —
  // titled session, sessions cache seeded BEFORE navigating — but the chat is
  // handed a DEFERRED `discussSeed` (command + row title/excerpt), not an
  // auto-fired command. The shell docks the item as a context card and holds
  // the discuss_item payload until the user types and sends their first
  // message; the client still ships only (kind, item_id) — the backend fetches
  // + seeds the item server-side (404 on missing/foreign).
  const handleDiscuss = useCallback(
    async (entry: FeedEntry) => {
      if (creatingSessionRef.current) return;
      creatingSessionRef.current = true;
      try {
        const session = await createSession(discussSessionTitle(entry.title));
        queryClient.setQueryData<ChatSession[]>(['chat-sessions', 'active'], (old) =>
          old ? [session, ...old] : [session]
        );
        void queryClient.invalidateQueries({ queryKey: ['chat-sessions', 'active'] });
        navigation.navigate('Chat', {
          screen: 'ChatScreen',
          params: {
            sessionId: session.id,
            discussSeed: discussSeedOf(entry),
          },
        });
      } catch (e) {
        showError((e as Error).message);
      } finally {
        creatingSessionRef.current = false;
      }
    },
    [navigation, queryClient]
  );

  if (inboxLoading || actionsLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }
  if (inboxError || actionsError) {
    return (
      <View style={styles.center}>
        <Text style={styles.empty}>
          {t('feed.failedToLoad', { defaultValue: 'Failed to load feed' })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('tabs.feed', { defaultValue: 'Feed' })}</Text>
        <TouchableOpacity
          onPress={() => markAllRead.mutate()}
          disabled={unreadCount === 0 || markAllRead.isPending}
          accessibilityRole="button"
        >
          <Text style={[styles.markAllRead, unreadCount === 0 && styles.markAllReadDisabled]}>
            {t('feed.markAllRead', { defaultValue: 'Mark all read' })}
          </Text>
        </TouchableOpacity>
      </View>
      <FeedList
        entries={entries}
        onOpenItem={openItem}
        onReviewThesis={handleReviewThesis}
        onDiscuss={handleDiscuss}
        refreshing={inboxRefetching || actionsRefetching}
        onRefresh={refetchAll}
      />
      <Text style={styles.disclaimer}>{t('common.advisoryDisclaimer')}</Text>
    </View>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.lg,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    markAllRead: { fontSize: theme.typography.fontSizeSm, color: theme.colors.primary },
    markAllReadDisabled: { color: theme.colors.textTertiary },
    center: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
      backgroundColor: theme.colors.background,
    },
    empty: { fontSize: theme.typography.fontSizeMd, color: theme.colors.textSecondary },
    disclaimer: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
      padding: theme.spacing.md,
    },
  });
}
